import { UniqueIdentifier } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";

import {
  ApiReportTemplateReportTypeChoices,
  ApiReportTemplateSectionFieldFieldTypeChoices,
  ReportPageLocationEnums,
  ReportSectionFieldTypeEnums,
  ReportTemplateGlobalsQuery,
  ReportTemplateSectionFieldInput,
  ReportTemplateSectionInput,
  ReportTemplateType,
} from "@api/graphql/types-and-hooks";
import {
  DiagnosisEvaluationPageLocations,
  InitialAbPageLocations,
  SinglePage,
} from "@pages/Templates/components/TemplateStructure/TemplateStructure";
import { ReportTemplateSection, ReportTemplateSectionField } from "@providers/TemplateEditorProvider";

export const getPageLocations = (reportType: ApiReportTemplateReportTypeChoices) => {
  return reportType === ApiReportTemplateReportTypeChoices.InitialAssessment
    ? Object.keys(InitialAbPageLocations)
    : reportType === ApiReportTemplateReportTypeChoices.DiagnosisEvaluation
      ? Object.keys(DiagnosisEvaluationPageLocations)
      : [SinglePage];
};

// Extracted out of TemplateEditor.tsx (rather than kept inline in handleSave, or exported
// from that file) so it can be unit-tested directly and so exporting it doesn't trip
// react-refresh/only-export-components on a component file.
// `satisfies` makes a key added to the input fail to compile until the save sends it.
export const mapSectionFieldToInput = (field: ReportTemplateSectionField): ReportTemplateSectionFieldInput =>
  ({
    id: field.id,
    title: field.title,
    rankOrder: field.rankOrder,
    fieldType: field.fieldType as unknown as ReportSectionFieldTypeEnums,
    isRequired: field.isRequired,
    isAuditable: field.isAuditable,
    content: field.content,
  }) satisfies Record<keyof ReportTemplateSectionFieldInput, unknown>;

// What a save sends for the sections, so the editor can also tell whether the current copy still
// matches what it sent.
export const toSectionInputs = (sections: ReportTemplateSection[]): ReportTemplateSectionInput[] =>
  sections.map((section) => ({
    id: section.id,
    rankOrder: section.rankOrder,
    pageLocation: section.pageLocation as unknown as ReportPageLocationEnums,
    title: section.title,
    sectionFields: section.fields.filter((field) => !field.isGlobal).map(mapSectionFieldToInput),
  }));

// The ids TemplateStructure gives items that aren't saved yet: `section-<ms>`, `field-<ms>`, and
// `field-<ms>-<copied id>`.
const TEMPORARY_ITEM_ID = /^(section|field)-\d+(-|$)/;

export const sendsTemporaryIds = (sent: ReportTemplateSectionInput[]): boolean =>
  sent.some(
    (section) =>
      TEMPORARY_ITEM_ID.test(section.id) ||
      (section.sectionFields ?? []).some((field) => !!field && TEMPORARY_ITEM_ID.test(field.id)),
  );

export const buildNewSectionField = ({
  id,
  rankOrder,
}: {
  id: string;
  rankOrder: number;
}): ReportTemplateSectionField => {
  // A key added to the save input fails to compile here (fieldType uses another enum).
  const savedKeys = {
    id,
    title: "New Field",
    rankOrder,
    isRequired: false,
    isAuditable: false,
    content: "",
  } satisfies Required<Omit<ReportTemplateSectionFieldInput, "fieldType">>;

  return {
    ...savedKeys,
    fieldType: ApiReportTemplateSectionFieldFieldTypeChoices.SimpleText,
    isDefault: false,
    isAi: false,
    placeholder: "",
    isEnabled: true,
    inputDependency: null,
  } as unknown as ReportTemplateSectionField;
};

type GlobalFieldNode = NonNullable<
  NonNullable<NonNullable<ReportTemplateGlobalsQuery["getReportTemplateGlobals"]>["edges"][number]>["node"]
>;

// Shared by the editor's load and its post-save refetch so both build sections the same way.
export const buildTemplateSections = (
  reportTemplate: ReportTemplateType,
  globalFieldNodes: (GlobalFieldNode | null | undefined)[],
): ReportTemplateSection[] =>
  (reportTemplate.reportTemplateSections?.edges ?? [])
    .map((edge) => {
      const sectionEdge = edge?.node;
      const sectionFieldEdges = sectionEdge?.reportTemplateSectionFields.edges || [];
      const sectionFields = sectionFieldEdges?.map((edge) => {
        const sectionFieldEdge = edge?.node;
        return {
          ...sectionFieldEdge,
          rankOrder: sectionFieldEdge?.rankOrder || 0,
          isDefault: sectionFieldEdge?.isDefault,
          isAi: !!sectionFieldEdge?.prompt,
          placeholder: sectionFieldEdge?.sampleData,
          isEnabled: false,
          inputDependency: sectionFieldEdge?.inputDependency || null,
        };
      });

      const existingDependencies = new Set(sectionFields.flatMap((field) => field.inputDependency || []));

      // Attach global fields if:
      // - the section has dependencies
      // - that dependency exists in globals
      // - no section field already uses it
      const attachedGlobals = globalFieldNodes
        .filter((globalField) => {
          const globalDeps = globalField?.inputDependency || [];
          const sectionDeps = sectionEdge?.inputDependency || [];

          const globalDepsArray = Array.isArray(globalDeps) ? globalDeps : [];
          const sectionDepsArray = Array.isArray(sectionDeps) ? sectionDeps : [];

          return globalDepsArray.some((dep) => sectionDepsArray.includes(dep) && !existingDependencies.has(dep));
        })
        .map((globalField) => ({
          ...globalField,
          isGlobal: true,
        }));
      const allFields = [...sectionFields, ...attachedGlobals];
      allFields.sort((f1, f2) => (f1?.rankOrder ?? 0) - (f2?.rankOrder ?? 0));

      return {
        ...sectionEdge,
        rankOrder: sectionEdge?.rankOrder || 0,
        isDefault: sectionEdge?.isDefault,
        fields: allFields as ReportTemplateSectionField[],
        inputDependency: sectionEdge?.inputDependency || null,
      };
    })
    .sort((s1, s2) => s1.rankOrder - s2.rankOrder) as ReportTemplateSection[];

// Temporary (editor-made) id -> the saved id the backend gave that item.
export type SavedIds = { sections: Map<string, string>; fields: Map<string, string> };

const takeFirst = <T>(pool: T[], matches: (item: T) => boolean): T | undefined => {
  const index = pool.findIndex(matches);
  return index === -1 ? undefined : pool.splice(index, 1)[0];
};

// The backend stores these values as sent, so a row that holds them reflects the save.
const sectionHolds = (row: ReportTemplateSection, sent: ReportTemplateSectionInput) =>
  String(row.pageLocation) === String(sent.pageLocation) &&
  row.rankOrder === (sent.rankOrder ?? 0) &&
  (row.title ?? "") === (sent.title ?? "");

const fieldHolds = (row: ReportTemplateSectionField, sent: ReportTemplateSectionFieldInput) =>
  row.rankOrder === (sent.rankOrder ?? 0) &&
  (row.title ?? "") === (sent.title ?? "") &&
  String(row.fieldType) === String(sent.fieldType) &&
  (row.content ?? "") === (sent.content ?? "") &&
  (sent.isRequired == null || row.isRequired === sent.isRequired) &&
  (sent.isAuditable == null || row.isAuditable === sent.isAuditable);

// The save returns no ids for the rows it creates, and the backend creates every item whose id it
// doesn't know (and every field of a new section). Pairs each of those sent items with the row the
// refetch returned for it, by the values it was sent with. Returns null unless the refetch holds
// exactly what was sent (a failed, stale, or partial refetch), so callers fail visibly instead of
// showing old values or resending temporary ids, which would create the rows again.
export const matchSavedIds = (
  sent: (ReportTemplateSectionInput | null | undefined)[],
  server: ReportTemplateSection[],
): SavedIds | null => {
  const sentSections = sent.filter((section): section is ReportTemplateSectionInput => !!section);
  const sections = matchSavedSections(sentSections, server);
  const fields = sections && matchSavedFields(sentSections, sections, server);
  return sections && fields ? { sections, fields } : null;
};

const matchSavedSections = (
  sentSections: ReportTemplateSectionInput[],
  server: ReportTemplateSection[],
): Map<string, string> | null => {
  const sentSectionIds = new Set(sentSections.map((section) => section.id));
  const serverSectionsById = new Map(server.map((section) => [section.id, section]));
  const createdSections = server.filter((section) => !sentSectionIds.has(section.id));

  const sections = new Map<string, string>();
  for (const section of sentSections) {
    const saved = serverSectionsById.get(section.id);
    if (saved) {
      if (!sectionHolds(saved, section)) return null;
      continue;
    }
    const match = takeFirst(createdSections, (created) => sectionHolds(created, section));
    if (!match) return null;
    sections.set(section.id, match.id);
  }
  return createdSections.length > 0 ? null : sections;
};

const matchSavedFields = (
  sentSections: ReportTemplateSectionInput[],
  savedSectionIds: Map<string, string>,
  server: ReportTemplateSection[],
): Map<string, string> | null => {
  const serverFields = server.flatMap((section) =>
    section.fields.filter((field) => !field.isGlobal).map((field) => ({ field, sectionId: section.id })),
  );
  const sentFields = sentSections.flatMap((section) =>
    (section.sectionFields ?? [])
      .filter((field): field is ReportTemplateSectionFieldInput => !!field)
      .map((field) => ({ field, sectionId: savedSectionIds.get(section.id) ?? section.id })),
  );
  const sentFieldIds = new Set(sentFields.map(({ field }) => field.id));
  const serverFieldsById = new Map(serverFields.map((entry) => [entry.field.id, entry]));
  const createdFields = serverFields.filter(({ field }) => !sentFieldIds.has(field.id));

  const fields = new Map<string, string>();
  for (const { field, sectionId } of sentFields) {
    const saved = serverFieldsById.get(field.id);
    if (saved) {
      if (saved.sectionId !== sectionId || !fieldHolds(saved.field, field)) return null;
      continue;
    }
    const match = takeFirst(
      createdFields,
      (created) => created.sectionId === sectionId && fieldHolds(created.field, field),
    );
    if (!match) return null;
    fields.set(field.id, match.field.id);
  }
  return createdFields.length > 0 ? null : fields;
};

// Finds the item an id held from before a save now goes by (a temporary id, or a saved row the save
// re-created), through the key every id the item has had shares. itemKeys maps saved id -> first id.
export const resolveItemId = (
  id: string,
  sections: ReportTemplateSection[],
  itemKeys: Record<string, string>,
): string => {
  const key = itemKeys[id] ?? id;
  const currentIds = sections.flatMap((section) => [section.id, ...section.fields.map((field) => field.id)]);
  return currentIds.find((currentId) => (itemKeys[currentId] ?? currentId) === key) ?? id;
};

// Carries edits made while a save was in flight onto the refetched copy. Rows the save created take
// their saved id and server-owned values (keys, defaults); the values a save sends, plus order,
// moves, deletes and items added since, come from the current copy.
export const rebaseOnServer = (
  current: ReportTemplateSection[],
  savedIds: SavedIds,
  server: ReportTemplateSection[],
): ReportTemplateSection[] => {
  const serverSections = new Map(server.map((section) => [section.id, section]));
  const serverFields = new Map(server.flatMap((section) => section.fields.map((field) => [field.id, field])));

  const rebaseField = (field: ReportTemplateSectionField): ReportTemplateSectionField => {
    const id = savedIds.fields.get(field.id) ?? field.id;
    const serverField = serverFields.get(id);
    if (field.isGlobal || !serverField) return field;
    const sentValues = {
      title: field.title,
      rankOrder: field.rankOrder,
      fieldType: field.fieldType,
      isRequired: field.isRequired,
      isAuditable: field.isAuditable,
      content: field.content,
    } satisfies Record<keyof Omit<ReportTemplateSectionFieldInput, "id">, unknown>;
    return { ...serverField, ...sentValues, id };
  };

  return current.map((section) => {
    const id = savedIds.sections.get(section.id) ?? section.id;
    const serverSection = serverSections.get(id);
    const fields = section.fields.map(rebaseField);
    if (!serverSection) return { ...section, fields };
    const sentValues = {
      pageLocation: section.pageLocation,
      rankOrder: section.rankOrder,
      title: section.title,
    } satisfies Record<keyof Omit<ReportTemplateSectionInput, "id" | "sectionFields">, unknown>;
    return { ...serverSection, ...sentValues, id, fields };
  });
};

type Drop = {
  // Current ids, resolved through any id swap a save made since the drag began.
  activeId: string;
  overId: string;
  // The drop target's own id, which names an empty page or section zone.
  rawOverId: UniqueIdentifier;
  isSectionDrag: boolean;
  isFieldDrag: boolean;
  resolveItemId: (id: string) => string;
};

// The sections after a drag ends, or null when the drop changes nothing.
export const sectionsAfterDrop = (sections: ReportTemplateSection[], drop: Drop): ReportTemplateSection[] | null => {
  const zone = typeof drop.rawOverId === "string" ? drop.rawOverId : "";
  if (drop.isSectionDrag && zone.startsWith("empty-page-")) {
    return moveSectionToPage(sections, drop.activeId, zone.replace("empty-page-", ""));
  }
  if (drop.isFieldDrag && zone.startsWith("empty-section-")) {
    return moveFieldToEmptySection(sections, drop.activeId, drop.resolveItemId(zone.replace("empty-section-", "")));
  }
  if (drop.activeId === drop.overId) return null;
  return drop.isSectionDrag
    ? reorderSection(sections, drop.activeId, drop.overId)
    : moveField(sections, drop.activeId, drop.overId);
};

const moveSectionToPage = (sections: ReportTemplateSection[], activeId: string, pageLocation: string) =>
  sections.map((section) =>
    section.id === activeId
      ? { ...section, pageLocation: pageLocation as typeof section.pageLocation, rankOrder: 1 }
      : section,
  );

const moveFieldToEmptySection = (sections: ReportTemplateSection[], activeId: string, targetSectionId: string) => {
  const newSections = [...sections];
  const sourceSection = newSections.find((section) => section.fields.some((field) => field.id === activeId));
  const targetSection = newSections.find((section) => section.id === targetSectionId);
  if (!sourceSection || !targetSection || sourceSection === targetSection || targetSection.fields.length !== 0) {
    return null;
  }

  const fieldIndex = sourceSection.fields.findIndex((field) => field.id === activeId);
  const [movedField] = sourceSection.fields.splice(fieldIndex, 1);
  sourceSection.fields = sourceSection.fields.map((field, idx) => ({ ...field, rankOrder: idx + 1 }));
  targetSection.fields = [{ ...movedField, rankOrder: 1 }];
  return newSections;
};

const reorderSection = (sections: ReportTemplateSection[], activeId: string, overId: string) => {
  const oldIndex = sections.findIndex((item) => item.id === activeId);
  const newIndex = sections.findIndex((item) => item.id === overId);
  // arrayMove with -1 would move the last section instead.
  if (oldIndex === -1 || newIndex === -1) return null;

  const destinationPageLocation = sections[newIndex].pageLocation;
  const reorderedSections = arrayMove(sections, oldIndex, newIndex);
  // The dragged section takes the page of the section it was dropped on.
  const pageOf = (section: ReportTemplateSection) =>
    section.id === activeId && destinationPageLocation ? destinationPageLocation : section.pageLocation;
  return reorderedSections.map((section, index) => {
    const page = section.id === activeId ? destinationPageLocation : section.pageLocation;
    // Sections are already in order, so the rank is the count on that page so far.
    const rankOrder = reorderedSections.slice(0, index + 1).filter((s) => pageOf(s) === page).length;
    return { ...section, pageLocation: pageOf(section), rankOrder };
  });
};

const moveField = (sections: ReportTemplateSection[], activeId: string, overId: string) => {
  const newSections = [...sections];
  const sourceSection = newSections.find((section) => section.fields.some((field) => field.id === activeId));
  const destinationSection = newSections.find((section) => section.fields.some((field) => field.id === overId));
  if (!sourceSection || !destinationSection) return null;

  const oldIndex = sourceSection.fields.findIndex((item) => item.id === activeId);
  const newIndex = destinationSection.fields.findIndex((item) => item.id === overId);
  const [movedField] = sourceSection.fields.splice(oldIndex, 1);
  destinationSection.fields.splice(newIndex, 0, movedField);
  destinationSection.fields = destinationSection.fields.map((field, idx) => ({ ...field, rankOrder: idx + 1 }));
  return newSections;
};
